import { useLDClient } from 'launchdarkly-react-client-sdk'
import LogRocket from 'logrocket'
import setupLogRocketReact from 'logrocket-react'
import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom-v5-compat'
import { ToastContainer, Zoom } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { useLazyGenerateIntercomTokenQuery } from 'api/intercom/generateToken'
import { useLazyGetBusinessInfoQuery } from 'api/settings/getBusinessInfo'
import { useLazyGetUserByIdQuery } from 'api/settings/getUserById'
import { useLazyGetTenantSettingsQuery } from 'api/tenant-settings/tenantSettingsCrud'
import { useAuthValue } from 'auth/AuthContext'
import { buildIntercomParams } from 'components/common/buildIntercomParams'
// eslint-disable-next-line import/no-cycle -- pre-existing components/common <-> components/header cycle, not introduced here
import Header from 'components/header/Header'
import { BEAMER_SELECTOR_ID } from 'components/header/settings'
import {
  NOTIFICATION_TOAST_CONTAINER_ID,
  NOTIFICATION_TOAST_WIDTH,
} from 'components/notifications/notification-center/notificationCenterConstants'
import NotificationToastCloseButton from 'components/notifications/notification-center/NotificationToastCloseButton'
import NotificationToastStyles from 'components/notifications/notification-center/NotificationToastStyles'
import ScreenPop from 'components/screen-pop/ScreenPop'
import Constants from 'Constants'
import { selectIsDialstackScreenPopEnabled } from 'data/tenant/tenantSettingsSelectors'
import { getUserInfo } from 'middleware/actions/login'
import { AccountingLockProvider } from 'providers/AccountingLockProvider'
import { useImpersonationSession } from 'providers/ImpersonationProvider'
import routes, { isPublicURL } from 'router/routes'
import { TOAST_DISMISS_TIMEOUT } from 'settings/constants/toast'
import { initializeBeamer } from 'utils/beamer'
import { getUnixTimestamp } from 'utils/date'
import { handleError } from 'utils/error'
import { initializeIntercom, updateIntercom } from 'utils/intercom'
import { getPageTitle } from 'utils/seo'

import GlobalDialogs from './GlobalDialogs'
import Loader from './loader'
import PreLoadImages from './PreLoadImages'

import type { User } from 'firebase/auth'
import type { UserInfo } from 'types/entities/UserInfo'

const { BEAMER_APP_ID, LOGROCKET_ID, DEV_MODE } = Constants

interface AuthContextValue {
  currentUser: User | null
  isInitialized: boolean
}

interface AppState {
  Appbar?: { showAppBar?: boolean }
  ResponseReducer?: { isLoading?: boolean }
  AuthReducer?: { userInfo?: UserInfo | null }
}

interface MainContainerProps {
  children: React.ReactNode
}

const MainContainer = ({ children }: MainContainerProps) => {
  const ldClient = useLDClient()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const [isValidUser, setIsValidUser] = useState(false)
  const authValue = useAuthValue() as AuthContextValue | undefined
  const currentUser = authValue?.currentUser ?? null
  const isInitialized = authValue?.isInitialized ?? false
  const { isImpersonating } = useImpersonationSession() as { isImpersonating: boolean }
  const showAppBar = useSelector((state: AppState) => state?.Appbar?.showAppBar, shallowEqual)
  const isLoading = useSelector((state: AppState) => state?.ResponseReducer?.isLoading ?? false, shallowEqual)
  const userInfo = useSelector((state: AppState) => state?.AuthReducer?.userInfo ?? null, shallowEqual)
  const isScreenPopEnabled = useSelector(selectIsDialstackScreenPopEnabled)

  const { pathname } = location
  const title = useMemo(() => getPageTitle(pathname), [pathname])
  const isPublicPath = isPublicURL(pathname)

  const [getUserDetailsById] = useLazyGetUserByIdQuery()
  const [getBusinessInfo] = useLazyGetBusinessInfoQuery()
  const [getTenantSettings, { isLoading: isTenantSettingsLoading }] = useLazyGetTenantSettingsQuery()
  const [generateIntercomToken] = useLazyGenerateIntercomTokenQuery()

  const initiateIntercomBeamerAndLogRocket = async () => {
    const userId = userInfo?.userId
    if (!userId) return

    const [userDetailsResult, businessInfoResult, intercomTokenResult] = await Promise.allSettled([
      getUserDetailsById({ id: userId }).unwrap(),
      getBusinessInfo(undefined).unwrap(),
      generateIntercomToken().unwrap(),
    ])

    if (userDetailsResult.status === 'rejected') {
      handleError(userDetailsResult.reason)
      console.warn('[Intercom] Failed to fetch user details — phone and createdAt will be missing.', userDetailsResult.reason)
    }
    if (businessInfoResult.status === 'rejected') {
      handleError(businessInfoResult.reason)
      console.warn('[Intercom] Failed to fetch business info — user will appear without company.', businessInfoResult.reason)
    }
    if (intercomTokenResult.status === 'rejected') {
      handleError(intercomTokenResult.reason)
      console.warn('[Intercom] Failed to generate identity JWT — identity verification disabled.', intercomTokenResult.reason)
    }

    const userDetails = userDetailsResult.status === 'fulfilled' ? userDetailsResult.value : undefined
    const businessInfo = businessInfoResult.status === 'fulfilled' ? businessInfoResult.value : undefined
    const intercomTokenResponse = intercomTokenResult.status === 'fulfilled' ? intercomTokenResult.value : undefined

    const firstName = userDetails?.user?.firstName ?? userInfo?.firstName ?? ''
    const lastName = userDetails?.user?.lastName ?? userInfo?.lastName ?? ''

    const intercomParams = buildIntercomParams({
      userId,
      firstName,
      lastName,
      email: userDetails?.user?.email ?? currentUser?.email ?? '',
      phone: userDetails?.user?.phoneNumber ?? null,
      createdAt: getUnixTimestamp(userDetails?.user?.createdAt),
      companyId: userInfo?.businessInfo?.businessId,
      companyName: userInfo?.businessInfo?.businessName ?? businessInfo?.businessTitle,
      companyWebsite: businessInfo?.website,
      companyPortalUrl: `${userInfo?.businessInfo?.businessName}.haulerhero.com`,
      intercomUserJwt: intercomTokenResponse?.token ?? null,
    })

    updateIntercom(intercomParams)
    // Beamer otherwise injects a launcher of its own, placed from its dashboard at the viewport's
    // top-right — over the avatar, and under the app bar where it cannot be clicked. Pinning the
    // offsets then keeps its unread counter on the flame instead of the icon beside it.
    initializeBeamer(BEAMER_APP_ID, {
      selector: `#${BEAMER_SELECTOR_ID}`,
      button: false,
      top: 0,
      right: 0,
      user_id: userId,
      user_firstname: firstName,
      user_lastname: lastName,
      user_email: intercomParams.email,
    })
    if (LOGROCKET_ID && !DEV_MODE) {
      LogRocket.init(LOGROCKET_ID)
      setupLogRocketReact(LogRocket)
      const lrTraits: Record<string, string | number | boolean> = {}
      if (intercomParams.name) lrTraits.name = intercomParams.name
      if (intercomParams.email) lrTraits.email = intercomParams.email
      if (intercomParams.phone) lrTraits.phone = intercomParams.phone
      LogRocket.identify(userId, lrTraits)
    }
  }

  useEffect(() => {
    if (!currentUser || isPublicPath) {
      setIsValidUser(false)
      return
    }

    dispatch(
      getUserInfo(async (response: unknown) => {
        await getTenantSettings(undefined).unwrap().catch(handleError)
        setIsValidUser(true)
        if (!response) {
          navigate(routes.app.error)
        }
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps -- auth/path-gated init; dispatch & RTKQ triggers are stable
  }, [currentUser, isPublicPath])

  useEffect(() => {
    if (!isValidUser) return

    // Individual targeting using username, we can also use the withLDConsumer'
    ldClient?.identify({
      kind: 'multi',
      user: { key: userInfo?.username },
      'crm-tenant': { key: userInfo?.businessInfo?.businessId },
    })

    // Intercom, Beamer, and LogRocket identify the logged-in operator by their CRM user
    // record/email. An impersonation session has no such record, so these calls 400; skip them.
    if (!isImpersonating) {
      initiateIntercomBeamerAndLogRocket()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once user is validated; reads latest userInfo/impersonation by closure
  }, [isValidUser])

  useEffect(() => {
    // Skip booting Intercom during impersonation — the session has no Intercom identity (we don't
    // identify the impersonator), so the widget would just ping unauthenticated and 403.
    if (!isImpersonating) {
      initializeIntercom()
    }
  }, [isImpersonating])

  if (!isInitialized) {
    return <Loader sx={{}} />
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {(isLoading || isTenantSettingsLoading) && <Loader sx={{}} />}

      {(!currentUser || isPublicPath) && children}

      {currentUser && isValidUser && !isPublicPath && (
        <AccountingLockProvider>
          {showAppBar && <Header />}
          <div className="main">{children}</div>
          {Constants.DIALSTACK_BRIDGE_URL && isScreenPopEnabled && <ScreenPop />}
        </AccountingLockProvider>
      )}

      <ToastContainer
        hideProgressBar
        newestOnTop
        pauseOnFocusLoss
        autoClose={TOAST_DISMISS_TIMEOUT}
        closeButton={false}
        closeOnClick={false}
        draggable={false}
        icon={false}
        pauseOnHover={false}
        position="top-center"
        rtl={false}
        theme="colored"
        transition={Zoom}
      />

      {/* Notification toasts get their own container so their look is set once, apart from the
          coloured "saved" toasts above; top-right keeps the two from stacking into each other. */}
      <ToastContainer
        hideProgressBar
        newestOnTop
        pauseOnHover
        autoClose={TOAST_DISMISS_TIMEOUT}
        closeButton={NotificationToastCloseButton}
        closeOnClick={false}
        containerId={NOTIFICATION_TOAST_CONTAINER_ID}
        draggable={false}
        position="top-right"
        style={{ width: NOTIFICATION_TOAST_WIDTH, maxWidth: '90vw' }}
        theme="light"
        transition={Zoom}
      />
      <NotificationToastStyles />
      <PreLoadImages />
      <GlobalDialogs />
    </>
  )
}

export default MainContainer
